// @flow
import moment from 'moment'
import type { Location } from './location'
import type { Price, NormalizedDisplayPrice } from './price'

export type CancellationPolicy = {|
  notice: number,
  refundPercent: number,
  penaltyPrice?: Price,
|}

export type Amenity = {|
  id: string,
  name: string,
  category: string,
  imageUrl: string,
  included: boolean,
  selected: boolean,
  price: {|
    total: string,
    raw: number,
  |},
|}

export type Provider = {|
  logoUrl: string,
  name: string,
  realName: string,
  email: string,
  phoneNumber: string,
  rating: number,
  ratingFloat: number,
  ratingCount: number,
  supplierScore: number | null,
|}

export type Vehicle = {|
  type: {|
    id: number,
    key: string,
    name: string,
  |},
  vehicleClass: ?number,
  vehicleClassName: string,
  numVehicles: number,
  maxPassengers: number,
  maxBags: number,
  imageUrl: string,
  isMaxBagsPerPerson: boolean,
  make: string,
  model: string,
  totalBags: number,
|}

export type Schedule = {|
  arrivalDatetime: moment,
  departureDatetime: moment,
  duration: {| value: number |},
  price: NormalizedDisplayPrice,
  transfers: number,
|}

export type TicketType = {|
  isDefault: boolean,
  defaultScheduleId: ?number,
  schedules: Array<Schedule>,
  ticketId: string,
  ticketName: string,
  validity: string,
  label: string,
  tooltip: string,
  validUntil: moment,
  validUntilForHumans: moment,
  lowestPrice: ?NormalizedDisplayPrice,
  price: ?NormalizedDisplayPrice,
  count: number,
|}

export type ServiceHours = {|
  startTime?: moment,
  endTime?: moment,
|}

export type HourlyDetails = {|
  includedKilometersPerHour: number,
  totalIncludedKilometers: number,
  originalRequested?: number,
  minimum: number,
  hoursRequested: number,
|}

export type BookingDetails = {|
  showTollsAndGratuity: boolean,
  showFlightInfo: boolean,
  termsOfServiceURL: string,
  gratuityIncluded: boolean,
  acceptsGratuity: boolean,
  meetAndGreetDefault: boolean,
  meetAndGreetAvailable: boolean,
  meetAndGreetPrice: string,
  meetAndGreetRawPrice: number,
  cancellationPolicy: Array<CancellationPolicy>,
  cancellableOffline: boolean,
  cancellableOnline: boolean,
  amendable: boolean,
  tollsIncluded: boolean,
  isBookable: boolean,
  startAddressIncomplete: boolean,
  endAddressIncomplete: boolean,
  waitTime: number,
  startTime: ?string,
  endTime: ?string,
  notes: string,
  extraPaxRequired: boolean,
  areTermsInternal: boolean,
  specialInstructionsPlaceholder: ?string,
  hourlyDetails: ?HourlyDetails,
  oauthAuthorizeUrl?: string,
  askPayment?: boolean,
|}

export type BaseStepLocation = {|
  distance: number,
  station?: ?Location,
  location?: ?Location,
|}

export type StepDistance = {|
  meters: number,
  miles: number,
|}

export type Step = {|
  from: BaseStepLocation,
  to: BaseStepLocation,
  distance?: StepDistance,
  description: string,
  main: boolean,
  type: string,
  alternativeTimes: Array<string>,
  arrivalTimes?: Array<string>,
  alternativeTimesIndex: number,
  arrivalDatetime?: moment,
  departureDatetime?: moment,
  lineName?: string,
  lineColor?: string,
  stops?: number,
  airline?: string,
  flight?: string,
  ship?: string,
  cruise?: string,
  bookingDetails: BookingDetails,
  vehicle: Vehicle,
  provider: Provider,
  amenities: Array<Amenity>,
  passengersCount: number,
  accurateTime: ?number,
  serviceHours: ?ServiceHours,
  ticketTypes: Array<TicketType>,
  passTypes: Array<TicketType>,
|}

/**
 * Returns whether all steps are bookeable
 * @param {Array<Object>} Steps
 * @private
 * @return {Boolean}
 */
export const isBookable = (steps: Array<Step>): boolean =>
  steps.every((step) => (step.main ? step.bookingDetails.isBookable : false))

/**
 * Returns whether all steps are cancellable online
 * @param {Array<Object>} Steps
 * @return {Boolean}
 * @private
 */
export const isCancelationFree = (steps: Array<Step>): boolean =>
  steps.every((step) => {
    const bookingDetails = step && step.bookingDetails

    // Free to cancel if no booking details (nothing to book - nothing to cancel)
    if (!bookingDetails) return true

    // Do no free to cancel if not cancelable online
    if (!bookingDetails.cancellableOnline) return false

    // Free to cancel if no cancellation policy with penalty or
    // if no policy with less than 100% refunc
    const cancelPolicy = (bookingDetails.cancellationPolicy || []).find(
      (p) => p.refundPercent < 100 || !!p.penaltyPrice
    )
    return !cancelPolicy
  })

/**
 * By given list of steps return trip duration
 * @return {number} Accurate time
 * @private
 */
export const getAccurateTime = (steps: Array<Step>): number =>
  steps.reduce((prev, step) => prev + (step.accurateTime || 0), 0)

/**
 * Returns main step from a list of steps. If no main step found
 * throw an error.
 * @return {Step}
 */
export const getMainStep = (steps?: Array<Step>): Step => {
  const result = steps && steps.find((x) => x.main)
  if (!result) {
    throw new Error('No main step found in provided steps')
  }
  return result
}
